import { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Typography,
} from "antd";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createAsset,
  retrieveAssets,
  selectAsset,
  deleteAsset,
  updateAsset,
} from "../../store/Assets/AssetSlice.js";
import { getJwtToken } from "../../utilities/authentication";
import { useCrudAccess } from "../../hooks/usePageAccess";
import useTranslation from "../../localization/useTranslation";
import appToast from "../../utilities/appToast";
import formatCurrency from "../../utilities/formatCurrency";
import ListPageShell from "../../components/Layout/ListPageShell";
import ResponsiveEntityTable from "../../components/List/ResponsiveEntityTable";

const Assets = () => {
  let { assets, loading, selectedAsset, error, successMessage, total } =
    useSelector((state) => state.assets);
  const [edit, setEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { canList, canCreate, canEdit, canDelete } = useCrudAccess("assets");
  const { t, language } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState("");
  const [searchNotes, setSearchNotes] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({ name: "", notes: "" });
  const filtersDebounceRef = useRef(null);

  const assetRows = (Array.isArray(assets) ? assets : []).map((asset) => {
    return { ...asset, key: asset.id };
  });

  const columns = [
    {
      title: t("assets.table.name"),
      dataIndex: "name",
    },
    {
      title: t("assets.table.cost"),
      dataIndex: "cost",
      render: (value, record) => (
        <Typography.Text>
          {formatCurrency(value, record.currency ?? "EGP", language)}
        </Typography.Text>
      ),
    },
    {
      title: t("assets.table.notes"),
      dataIndex: "notes",
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      title: t("assets.table.actions"),
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <Row gutter={3}>
          {canEdit && (
            <Col>
              <Button
                icon={<EditOutlined />}
                type="primary"
                onClick={() => editModal(record)}
              >
                {t("common.edit")}
              </Button>
            </Col>
          )}
          {canDelete && (
            <Col>
              <Popconfirm
                title={t("assets.confirm.deleteTitle")}
                description={t("assets.confirm.deleteMessage")}
                onConfirm={() => handleDeleteAsset(record.id)}
                okText={t("common.deleteConfirm")}
                cancelText={t("common.back")}
              >
                <Button icon={<DeleteOutlined />} type="primary" danger>
                  {t("common.delete")}
                </Button>
              </Popconfirm>
            </Col>
          )}
        </Row>
      ),
    });
  }

  const handleFinish = (values) => {
    if (edit && !canEdit) {
      return;
    }

    if (!edit && !canCreate) {
      return;
    }

    edit ? handleUpdateAsset(values) : handleAddAsset(values);
    setOpen(false);
  };

  const handleAddAsset = (values) => {
    if (!canCreate) {
      return;
    }

    const obj = {
      values,
      token: getJwtToken(),
    };

    dispatch(createAsset(obj));
  };

  const handleUpdateAsset = (values) => {
    if (!canEdit) {
      return;
    }

    values = {
      ...values,
      id: selectedAsset.id,
    };

    dispatch(
      updateAsset({
        payload: values,
        token: getJwtToken(),
      }),
    );
  };

  const handleDeleteAsset = (id) => {
    if (!canDelete) {
      return;
    }

    dispatch(deleteAsset({ id }));
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const initFetch = useCallback(
    (page = 1, per_page = 10, filters = {}) => {
      if (!canList) {
        return;
      }

      const token = getJwtToken();
      const params = { page, per_page };
      if (filters.name) params.name = filters.name;
      if (filters.notes) params.notes = filters.notes;

      dispatch(retrieveAssets({ ...params, token }));
    },
    [dispatch, canList],
  );

  // This is the only effect that requests the list.
  useEffect(() => {
    initFetch(currentPage, pageSize, appliedFilters);
  }, [initFetch, currentPage, pageSize, appliedFilters]);

  useEffect(() => {
    if (successMessage) {
      appToast.success(successMessage);
      initFetch(currentPage, pageSize, appliedFilters);
    }
  }, [successMessage, initFetch, currentPage, pageSize, appliedFilters]);

  // Debounce the search inputs into applied filters. Returning the previous
  // object when nothing changed keeps React from re-running the fetch effect.
  useEffect(() => {
    if (filtersDebounceRef.current) {
      clearTimeout(filtersDebounceRef.current);
    }

    filtersDebounceRef.current = setTimeout(() => {
      setAppliedFilters((prev) => {
        if (prev.name === searchName && prev.notes === searchNotes) {
          return prev;
        }
        setCurrentPage(1);
        return { name: searchName, notes: searchNotes };
      });
    }, 400);

    return () => clearTimeout(filtersDebounceRef.current);
  }, [searchName, searchNotes]);

  if (!canList) {
    return (
      <Alert message={t("assets.alert.noAccess")} type="warning" showIcon />
    );
  }

  const handleAddModal = () => {
    if (!canCreate) {
      return;
    }

    form.setFieldsValue({
      name: "",
      cost: undefined,
      notes: "",
    });
    setEdit(false);
    setOpen(true);
  };

  const editModal = (record) => {
    if (!canEdit) {
      return;
    }

    form.setFieldsValue({
      ...record,
    });
    dispatch(selectAsset(record));
    setEdit(true);
    setOpen(true);
  };

  const modalUI = () => {
    return (
      <Modal
        title={t("assets.modal.title")}
        open={open}
        onCancel={handleCancel}
        footer={[
          <Button key="Cancel" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          onFinish={handleFinish}
          dir="rtl"
          labelCol={{ span: 24 }}
        >
          <Form.Item
            name="name"
            label={t("assets.form.name.label")}
            rules={[
              {
                required: true,
                message: t("assets.form.name.required"),
              },
            ]}
          >
            <Input placeholder={t("assets.form.name.placeholder")} />
          </Form.Item>

          <Form.Item
            name="cost"
            label={t("assets.form.cost.label")}
            rules={[
              {
                required: true,
                message: t("assets.form.cost.required"),
              },
              {
                type: "number",
                min: 1,
                message: t("assets.form.cost.min"),
              },
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              placeholder={t("assets.form.cost.placeholder")}
            />
          </Form.Item>

          <Form.Item name="notes" label={t("assets.form.notes.label")}>
            <Input.TextArea placeholder={t("assets.form.notes.placeholder")} />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={loading}>
              {edit ? t("common.edit") : t("common.add")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };
  return (
    <>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {modalUI()}

      <ListPageShell
        icon={<WalletOutlined />}
        title={t("assets.title") || t("sidebar.assets") || "Assets"}
        subtitle={
          t("assets.subtitle") ||
          "Track laboratory assets, purchase costs, and notes"
        }
        count={total}
        countLabel={t("assets.title") || t("sidebar.assets") || "Assets"}
        actions={
          canCreate ? (
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={handleAddModal}
            >
              {t("assets.button.add")}
            </Button>
          ) : null
        }
        filters={
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                placeholder={
                  t("assets.search.namePlaceholder") || "Search by name"
                }
                allowClear
                onSearch={(v) => setSearchName(v)}
                onChange={(e) => setSearchName(e.target.value)}
                style={{ width: "100%" }}
                value={searchName}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Input.Search
                placeholder={
                  t("assets.search.notesPlaceholder") || "Search by notes"
                }
                allowClear
                onSearch={(v) => setSearchNotes(v)}
                onChange={(e) => setSearchNotes(e.target.value)}
                style={{ width: "100%" }}
                value={searchNotes}
              />
            </Col>
          </Row>
        }
        filtersActiveCount={[searchName, searchNotes].filter(Boolean).length}
        onResetFilters={() => {
          setSearchName("");
          setSearchNotes("");
        }}
      >
        <ResponsiveEntityTable
          loading={loading}
          columns={columns}
          dataSource={assetRows}
          scroll={{ x: 720 }}
          mobileTitleKey="name"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "15", "20", "50"],
          }}
          onChange={(pagination) => {
            if (pagination?.pageSize && pagination.pageSize !== pageSize) {
              setPageSize(pagination.pageSize);
              setCurrentPage(1);
              return;
            }
            if (pagination?.current) {
              setCurrentPage(pagination.current);
            }
          }}
        />
      </ListPageShell>
    </>
  );
};

export default Assets;
